/* eslint-disable react/jsx-filename-extension */
/**
 * This is a wrapper component for different Layouts.
 * Navigational 'aside' content should be added to this wrapper.
 */
import React, { useEffect } from 'react';

import useMediaQuery from 'hooks/useMediaQuery';
import useMounted from 'hooks/useMounted';

import { createGlobalState } from './hookGlobalState';
import TabNavByUserType from './TabNavByUserType';

const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

// Add global state for tab scrolling effect
const initialScrollState = { scrollLeft: 0 };
const { useGlobalState } = createGlobalState(initialScrollState);

// Horizontal scroll animation using element.scrollTo()
const scrollToTab = (currentPage, scrollLeft, setScrollLeft) => {
  const el = document.querySelector(`#${currentPage}Tab`);

  if (el) {
    // el.scrollIntoView doesn't work with Safari and it considers vertical positioning too.
    // This scroll behaviour affects horizontal scrolling only
    // and it expects that the immediate parent element is scrollable.
    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const maxScrollDistance = parent.scrollWidth - parentRect.width;

    const hasParentScrolled = parent.scrollLeft > 0;
    const scrollPositionCurrent = hasParentScrolled ? parent.scrollLeft : scrollLeft;

    const tabRect = el.getBoundingClientRect();
    const diffLeftBetweenTabAndParent = tabRect.left - parentRect.left;
    const tabScrollPosition = parent.scrollLeft + diffLeftBetweenTabAndParent;

    const scrollPositionNew =
      tabScrollPosition > maxScrollDistance
        ? maxScrollDistance
        : parent.scrollLeft + diffLeftBetweenTabAndParent;

    const needsSmoothScroll = scrollPositionCurrent !== scrollPositionNew;

    if (parent.scrollTo && (!hasParentScrolled || (hasParentScrolled && needsSmoothScroll))) {
      // Ensure that smooth scroll animation uses old position as starting point after navigation.
      parent.scrollTo({ left: scrollPositionCurrent });
      // Scroll to new position
      parent.scrollTo({ left: scrollPositionNew, behavior: 'smooth' });
    }
    // Always keep track of new position (even if smooth scrolling is not applied)
    setScrollLeft(scrollPositionNew);
  }
};

/**
 * Side nav with navigation to different account settings.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.currentPage
 * @returns {JSX.Element} Side nav with navigation to different account settings
 */
const LayoutWrapperAccountSettingsSideNav = (props) => {
  const mounted = useMounted();
  const [scrollLeft, setScrollLeft] = useGlobalState('scrollLeft');
  const hasHorizontalTabLayout = useMediaQuery(
    `(max-width: ${MAX_HORIZONTAL_NAV_SCREEN_WIDTH}px)`,
    true
  );

  const { currentPage } = props;

  useEffect(() => {
    if (hasHorizontalTabLayout && mounted) {
      scrollToTab(currentPage, scrollLeft, setScrollLeft);
    }
  }, [currentPage, hasHorizontalTabLayout, scrollLeft, setScrollLeft, mounted]);

  return <TabNavByUserType currentPage={currentPage} />;
};

export default LayoutWrapperAccountSettingsSideNav;
